import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Package, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const Checkout = () => {
  const { user } = useContext(AuthContext);
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    full_name: '',
    company: user?.company_name || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: user?.phone || ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCartAndProducts();
  }, []);

  const fetchCartAndProducts = async () => {
    try {
      const [cartResponse, productsResponse] = await Promise.all([
        axios.get(`${API}/cart`),
        axios.get(`${API}/products`)
      ]);
      
      if (!cartResponse.data.items || cartResponse.data.items.length === 0) {
        toast.error('Your cart is empty');
        navigate('/cart');
        return;
      }
      
      setCart(cartResponse.data);
      
      const productsMap = {};
      productsResponse.data.forEach(p => {
        productsMap[p.id] = p;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load checkout');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let totalCases = 0;
    let subtotal = 0;
    
    cart.items?.forEach(item => {
      const product = products[item.product_id];
      if (product) {
        totalCases += item.quantity;
        subtotal += product.price_per_case * item.quantity;
      }
    });
    
    let discountPercent = 0;
    if (totalCases >= 51) discountPercent = 10;
    else if (totalCases >= 11) discountPercent = 5;
    
    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;
    
    return { totalCases, subtotal, discountPercent, discountAmount, total };
  };

  const handlePlaceOrder = async () => {
    // Validate shipping address
    if (!shippingAddress.full_name || !shippingAddress.company || 
        !shippingAddress.address_line1 || !shippingAddress.city || 
        !shippingAddress.state || !shippingAddress.zip_code || !shippingAddress.phone) {
      toast.error('Please fill in all required shipping address fields');
      return;
    }

    setProcessing(true);
    
    try {
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;
      
      // Create checkout session with Stripe and shipping address
      const response = await axios.post(
        `${API}/payments/create-checkout-session?origin_url=${encodeURIComponent(originUrl)}`,
        { shipping_address: shippingAddress }
      );
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  const { totalCases, subtotal, discountPercent, discountAmount, total } = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Checkout</h1>
          <p className="text-lg text-gray-600">Review your order and complete purchase</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Order Items */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Order Items</h2>
              <div className="space-y-4">
                {cart.items.map(item => {
                  const product = products[item.product_id];
                  if (!product) return null;
                  
                  return (
                    <div key={item.product_id} className="flex gap-4 pb-4 border-b last:border-0" data-testid={`checkout-item-${item.product_id}`}>
                      <div className="bg-white rounded-lg w-16 h-16 flex items-center justify-center flex-shrink-0 border-2 border-gray-200 overflow-hidden">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <Package className="h-8 w-8 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{product.name}</h3>
                        <p className="text-sm text-gray-600">Quantity: {item.quantity} case(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">${(product.price_per_case * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Shipping Address</h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.full_name}
                      onChange={(e) => setShippingAddress({...shippingAddress, full_name: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="John Doe"
                      data-testid="shipping-full-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.company}
                      onChange={(e) => setShippingAddress({...shippingAddress, company: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="ABC Restaurant"
                      data-testid="shipping-company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={shippingAddress.address_line1}
                    onChange={(e) => setShippingAddress({...shippingAddress, address_line1: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="123 Main Street"
                    data-testid="shipping-address1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Apartment, suite, etc. (optional)
                  </label>
                  <input
                    type="text"
                    value={shippingAddress.address_line2}
                    onChange={(e) => setShippingAddress({...shippingAddress, address_line2: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="Suite 200"
                    data-testid="shipping-address2"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="New York"
                      data-testid="shipping-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="NY"
                      data-testid="shipping-state"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.zip_code}
                      onChange={(e) => setShippingAddress({...shippingAddress, zip_code: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                      placeholder="10001"
                      data-testid="shipping-zip"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none"
                    placeholder="(555) 123-4567"
                    data-testid="shipping-phone"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Billing Information</h2>
              <div className="space-y-3 text-gray-700">
                <p><strong>Company:</strong> {user.company_name}</p>
                <p><strong>Email:</strong> {user.email}</p>
                {user.phone && <p><strong>Phone:</strong> {user.phone}</p>}
                {user.business_type && <p><strong>Business Type:</strong> {user.business_type}</p>}
              </div>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-start">
                <CreditCard className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Payment Information</h3>
                  <p className="text-gray-700 text-sm">
                    This is a test environment. In production, Stripe payment processing would be integrated here. 
                    You can place the order to see the order confirmation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Cases</span>
                  <span className="font-semibold">{totalCases}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                
                {discountPercent > 0 && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-green-700 font-semibold">Bulk Discount ({discountPercent}%)</span>
                      <span className="text-green-700 font-bold">-${discountAmount.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                <div className="border-t-2 border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-blue-600" data-testid="checkout-total">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handlePlaceOrder}
                disabled={processing}
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                data-testid="place-order-btn"
              >
                {processing ? 'Redirecting to Payment...' : 'Proceed to Payment'}
              </Button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                By placing this order, you agree to our terms and conditions
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Checkout;